import { Inject, Injectable } from '@angular/core';
import { BehaviorSubject } from 'rxjs';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Token } from './auth.model';
import { Router } from '@angular/router';
import { AppConfig, CONFIG } from 'src/app/shared/models/config';
import { createCodeVerifier, createCodeChallenge } from 'src/app/shared/helpers/auth-utils/auth.utils';

const VERIFIER_STORAGE_KEY = 'login-code-verifier';
const CHALLENGE_STORAGE_KEY = 'login-code-challenge';
const CLIENT_ID = 'svebar-portal';
const AUTH_URL = `${CONFIG.authUrl}/auth/authorize`;

@Injectable({
  providedIn: 'root'
})
export class AuthService {
  private TOKEN_URL: string;
  private REDIRECT_URL: string;
  private _loggedIn = new BehaviorSubject<boolean>(false);
  public loggedIn$ = this._loggedIn.asObservable();
  /** 🔹 Channel used for cross-tab communication */
  private logoutChannel = new BroadcastChannel('auth_channel');

  constructor(@Inject('APP_CONFIG') private config: AppConfig,
    private rest: HttpClient, //private sessionService: SessionService,
    private router: Router) {
    console.log('AuthService ctor - initializing BroadcastChannel');
    this.logoutChannel = new BroadcastChannel('auth_channel');
    this.logoutChannel.onmessage = (event) => {
      console.log('auth_channel.onmessage', event.data);
      if (event.data === 'logout') {
        // run in Angular zone (see code below)
        this.performLogout(false);
      }
    };

    this.TOKEN_URL = `${this.config.authUrl}/auth/token`;
    this.REDIRECT_URL = `${this.config.webUrl}/logga-in`;
    this.loggedIn = this.token !== '';
    this.logoutChannel.onmessage = (event) => {
      if (event.data === 'logout') {
        console.log('🔄 Received logout event from another tab');
        this.performLogout(false);
      }};
  }

  set loggedIn(value: boolean) {
    this._loggedIn.next(value);
  }

  get loggedIn() {
   return this._loggedIn.value;

  }

  get token(): string {
    const _token = sessionStorage.getItem('token');
    const parsedToken: Token | null = (_token && JSON.parse(_token)) || null;
    return parsedToken?.access_token || '';
  }

  /** Initiates OAuth login attempt */
  async login() {
    const codeVerifier = createCodeVerifier();
    const codeChallenge = createCodeChallenge(codeVerifier);
    sessionStorage.setItem(VERIFIER_STORAGE_KEY, codeVerifier);
    sessionStorage.setItem(CHALLENGE_STORAGE_KEY, codeChallenge);
    const loginQuery = this.createLoginQuery(codeChallenge);
    window.location.assign(`${AUTH_URL}?${new URLSearchParams(loginQuery).toString()}`);

    this.saveSession({ access_token: 'token' } as Token);
    await this.router.navigate([]);
  }

  saveSession(token: Token) {
    sessionStorage.setItem('token', JSON.stringify(token));
    this.loggedIn = true;
  }

  clearSession() {
    this.loggedIn = false;
    sessionStorage.removeItem('token');
    this.router.navigate(['logga-in']);
  }

  private async performLogout(navigate: boolean = true, idleLogout?: boolean) {
    // Remove token for this tab
    try {
      sessionStorage.removeItem('token');
    } catch (e) {
      console.log("e");
    }

    // Notify subscribers in this tab
    this._loggedIn.next(false);

    // Navigate to login if desired
    if (navigate) {
      // use NgZone.run when called outside zone, but we are already in zone here
      await this.router.navigate(['logga-in']);
    }

    if (idleLogout) {
      setTimeout(() => alert('Du har blivit utloggad'), 200);
    }
  }

  /** Exchanges OAuth auth code for auth token and returns the token */
  async getTokenFromAuthCode(authCode: string) {
    const httpOptions = {
      headers: new HttpHeaders({ 'Content-Type': 'application/x-www-form-urlencoded' })
    };
    const codeVerifier = sessionStorage.getItem(VERIFIER_STORAGE_KEY);
    if (!codeVerifier) {
      throw Error('Ingen sparad codeverifier för inloggningssession');
    }
    const body = new URLSearchParams();
    body.set('grant_type', 'code');
    body.set('code', authCode);
    body.set('client_id', CLIENT_ID);
    body.set('code_verifier', codeVerifier);
    body.set('redirect_uri', `${location.origin}${this.REDIRECT_URL}`);
    return await this.rest.post<Token>(this.TOKEN_URL, body.toString(), httpOptions).toPromise();
  }

  /** Logout user */
  async logout(idleLogout?: boolean) {
    //this.sendGlobalLogoutEvent();
    console.log('Sending logout broadcast...');
    this.logoutChannel?.postMessage('logout');
    this.clearSession();
    this._loggedIn.next(false);
    this.router.navigate(['logga-in']).then(() => {
      setTimeout(() => {
        if (idleLogout) {
          alert("Du har blivit utloggad")
        }
      }, 200);
    });
  }

  private createLoginQuery(codeChallenge: string) {
    return {
      redirect_uri: `${location.origin}${this.REDIRECT_URL}`,
      client_id: CLIENT_ID,
      response_type: 'code',
      scope: 'openid profile email read:appointments',
      code_challenge: codeChallenge,
      code_challenge_method: 'S256',
      state: '/'
    };
  }
}
